/**
 * In-app dialogs.
 *
 * `prompt`, `confirm` and `alert` are the browser's, not the product's.
 * They render as an operating-system panel bolted to the top of the
 * window, carrying the host name - "facewallet.net says" - in a typeface
 * and a colour scheme nobody chose. Installed as a progressive web app
 * that is worse still: the illusion of an application breaks the first
 * time somebody changes a spending limit, and on some platforms the
 * dialog is suppressed entirely, so the code silently receives `null`
 * and the action never happens.
 *
 * These are the same three shapes, drawn inside the page: a question
 * that returns text, a question that returns yes or no, a statement, and
 * a choice from a fixed list. Each returns a promise, so a call site
 * reads the way the blocking original did.
 *
 * They inherit the host's colours through CSS variables, with fallbacks,
 * so the wallet and the two consoles each get their own look without
 * this file knowing anything about them.
 */

export interface AskOptions {
  title: string;
  /** Explanatory line under the title. */
  message?: string;
  value?: string;
  placeholder?: string;
  kind?: 'text' | 'number' | 'multiline';
  /** Numeric keypad on a phone. */
  inputMode?: 'numeric' | 'decimal' | 'text';
  maxLength?: number;
  /** Refuse to return until at least this many characters are typed. */
  minLength?: number;
  okLabel?: string;
  cancelLabel?: string;
  /** Paint the confirming button as destructive. */
  danger?: boolean;
}

export interface ConfirmOptions {
  title: string;
  message?: string;
  okLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export interface ChoiceOptions<T extends string> {
  title: string;
  message?: string;
  choices: { value: T; label: string; hint?: string }[];
  cancelLabel?: string;
}

const STYLE_ID = 'fw-dialog-style';

const CSS = `
.fwdlg{position:fixed;inset:0;z-index:9000;display:grid;place-items:center;padding:18px;
  background:rgba(6,12,10,.55);backdrop-filter:blur(2px);animation:fwdlgin .12s ease-out}
.fwdlg-box{width:100%;max-width:420px;max-height:calc(100vh - 36px);overflow:auto;
  background:var(--card,#fff);color:var(--ink,#0E1A15);
  border:1px solid var(--line,#DCE7E1);border-radius:16px;padding:20px;
  box-shadow:0 24px 60px rgba(0,0,0,.34);animation:fwdlgup .14s ease-out}
.fwdlg-t{font-size:16px;font-weight:700;line-height:1.35;margin:0}
.fwdlg-m{font-size:13px;line-height:1.5;color:var(--mut,#5C6F67);margin:8px 0 0}
.fwdlg-box input,.fwdlg-box textarea{width:100%;margin-top:14px;padding:11px 12px;font:inherit;font-size:15px;
  color:var(--ink,#0E1A15);background:var(--bg,#F2F7F4);
  border:1px solid var(--line,#DCE7E1);border-radius:10px;outline:none;box-sizing:border-box}
.fwdlg-box textarea{min-height:88px;resize:vertical}
.fwdlg-box input:focus,.fwdlg-box textarea:focus{border-color:var(--teal,#0E5F45)}
.fwdlg-err{font-size:12px;color:var(--red,#DC2626);margin:8px 0 0;display:none}
.fwdlg-row{display:flex;gap:8px;margin-top:16px}
.fwdlg-row button{flex:1;padding:11px 14px;font:inherit;font-size:14px;font-weight:600;
  border-radius:10px;cursor:pointer;border:1px solid var(--line,#DCE7E1);
  background:transparent;color:var(--ink,#0E1A15)}
.fwdlg-row button.fwdlg-ok{background:var(--teal,#0E5F45);border-color:var(--teal,#0E5F45);color:#fff}
.fwdlg-row button.fwdlg-danger{background:var(--red,#DC2626);border-color:var(--red,#DC2626);color:#fff}
.fwdlg-row button:hover{filter:brightness(1.06)}
.fwdlg-choices{display:flex;flex-direction:column;gap:8px;margin-top:14px}
.fwdlg-choices button{width:100%;text-align:left;padding:12px 14px;font:inherit;font-size:14px;
  border:1px solid var(--line,#DCE7E1);border-radius:10px;background:transparent;
  color:var(--ink,#0E1A15);cursor:pointer}
.fwdlg-choices button:hover{border-color:var(--teal,#0E5F45)}
.fwdlg-choices .fwdlg-hint{display:block;font-size:12px;color:var(--mut,#5C6F67);margin-top:3px}
@keyframes fwdlgin{from{opacity:0}to{opacity:1}}
@keyframes fwdlgup{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion:reduce){.fwdlg,.fwdlg-box{animation:none}}
`;

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.append(el);
}

/**
 * The shell every dialog shares.
 *
 * `settle` is handed to the builder so it can close and resolve. The
 * element that had focus is restored afterwards, because a dialog that
 * drops focus on the body leaves a keyboard user at the top of the page.
 */
function open<T>(build: (box: HTMLElement, settle: (value: T) => void) => void, onEscape: T): Promise<T> {
  ensureStyle();
  const previous = document.activeElement as HTMLElement | null;

  return new Promise<T>((resolve) => {
    const back = document.createElement('div');
    back.className = 'fwdlg';
    back.setAttribute('role', 'dialog');
    back.setAttribute('aria-modal', 'true');

    const box = document.createElement('div');
    box.className = 'fwdlg-box';
    back.append(box);

    let done = false;
    const settle = (value: T) => {
      if (done) return;
      done = true;
      document.removeEventListener('keydown', onKey, true);
      back.remove();
      previous?.focus?.();
      resolve(value);
    };

    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') { e.preventDefault(); settle(onEscape); return; }
      if (e.key !== 'Tab') return;
      // Keep the keyboard inside the dialog: tabbing out of a modal and
      // landing on the page behind it is how people end up typing a card
      // limit into a field they cannot see.
      const focusable = box.querySelectorAll<HTMLElement>('input,textarea,button,[tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    back.addEventListener('mousedown', (e) => { if (e.target === back) settle(onEscape); });
    document.addEventListener('keydown', onKey, true);

    build(box, settle);
    document.body.append(back);
  });
}

function heading(box: HTMLElement, title: string, message?: string): void {
  const h = document.createElement('p');
  h.className = 'fwdlg-t';
  h.textContent = title;
  box.append(h);
  if (message) {
    const m = document.createElement('p');
    m.className = 'fwdlg-m';
    m.textContent = message;
    box.append(m);
  }
}

/** A question that returns what was typed, or null if it was dismissed. */
export function ask(opts: AskOptions): Promise<string | null> {
  return open<string | null>((box, settle) => {
    heading(box, opts.title, opts.message);

    const field = opts.kind === 'multiline'
      ? document.createElement('textarea')
      : document.createElement('input');
    if (field instanceof HTMLInputElement) {
      field.type = opts.kind === 'number' ? 'text' : 'text';
      field.inputMode = opts.inputMode ?? (opts.kind === 'number' ? 'numeric' : 'text');
    }
    if (opts.maxLength) field.maxLength = opts.maxLength;
    field.value = opts.value ?? '';
    if (opts.placeholder) field.placeholder = opts.placeholder;
    box.append(field);

    const err = document.createElement('p');
    err.className = 'fwdlg-err';
    box.append(err);

    const row = document.createElement('div');
    row.className = 'fwdlg-row';
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.textContent = opts.cancelLabel ?? 'Cancel';
    cancel.onclick = () => settle(null);
    const ok = document.createElement('button');
    ok.type = 'button';
    ok.className = 'fwdlg-ok' + (opts.danger ? ' fwdlg-danger' : '');
    ok.textContent = opts.okLabel ?? 'OK';

    const submit = () => {
      const value = field.value.trim();
      if (opts.minLength && value.length < opts.minLength) {
        err.textContent = `Please write at least ${opts.minLength} characters. ${value.length} so far.`;
        err.style.display = 'block';
        field.focus();
        return;
      }
      settle(value);
    };
    ok.onclick = submit;
    field.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter' && opts.kind !== 'multiline') { e.preventDefault(); submit(); }
    });

    row.append(cancel, ok);
    box.append(row);
    setTimeout(() => { field.focus(); field.select?.(); }, 0);
  }, null);
}

/** Yes or no. Dismissing is no. */
export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  return open<boolean>((box, settle) => {
    heading(box, opts.title, opts.message);
    const row = document.createElement('div');
    row.className = 'fwdlg-row';
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.textContent = opts.cancelLabel ?? 'Cancel';
    cancel.onclick = () => settle(false);
    const ok = document.createElement('button');
    ok.type = 'button';
    ok.className = 'fwdlg-ok' + (opts.danger ? ' fwdlg-danger' : '');
    ok.textContent = opts.okLabel ?? 'Confirm';
    ok.onclick = () => settle(true);
    row.append(cancel, ok);
    box.append(row);
    setTimeout(() => ok.focus(), 0);
  }, false);
}

/** A statement with one way out. */
export function notify(opts: { title: string; message?: string; okLabel?: string }): Promise<void> {
  return open<void>((box, settle) => {
    heading(box, opts.title, opts.message);
    const row = document.createElement('div');
    row.className = 'fwdlg-row';
    const ok = document.createElement('button');
    ok.type = 'button';
    ok.className = 'fwdlg-ok';
    ok.textContent = opts.okLabel ?? 'Close';
    ok.onclick = () => settle(undefined);
    row.append(ok);
    box.append(row);
    setTimeout(() => ok.focus(), 0);
  }, undefined);
}

/**
 * One of a fixed set.
 *
 * This replaces the worst of the native prompts - the ones that listed
 * the permitted answers in the message and asked somebody to type one of
 * them correctly, where a typo silently became an invalid outcome.
 */
export function choose<T extends string>(opts: ChoiceOptions<T>): Promise<T | null> {
  return open<T | null>((box, settle) => {
    heading(box, opts.title, opts.message);
    const list = document.createElement('div');
    list.className = 'fwdlg-choices';
    for (const c of opts.choices) {
      const b = document.createElement('button');
      b.type = 'button';
      const label = document.createElement('span');
      label.textContent = c.label;
      b.append(label);
      if (c.hint) {
        const hint = document.createElement('span');
        hint.className = 'fwdlg-hint';
        hint.textContent = c.hint;
        b.append(hint);
      }
      b.onclick = () => settle(c.value);
      list.append(b);
    }
    box.append(list);
    const row = document.createElement('div');
    row.className = 'fwdlg-row';
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.textContent = opts.cancelLabel ?? 'Cancel';
    cancel.onclick = () => settle(null);
    row.append(cancel);
    box.append(row);
    setTimeout(() => (list.querySelector('button') as HTMLElement | null)?.focus(), 0);
  }, null);
}
